import type { ProductFilter, ProductQuery, SortOptions } from '~/types'

type CatalogSetup = {
    filter?: ProductFilter,
    sortBy?: SortOptions
    perPage?: number
}

export  const useCatalog = async (setup : CatalogSetup) => {
    const config = useRuntimeConfig()

    const route = useRoute();
    const router = useRouter();

    const userPreferences = useUserPreferences();

    const getRouteCategories = () : number[] => {
        const categories: number[] = [];

        if (route.query.categories) {
            const categoryParams = Array.isArray(route.query.categories) ? route.query.categories : [route.query.categories];
            
            categoryParams.forEach(cat => {
                const catId = Number(cat);
                if (!isNaN(catId)) {
                    categories.push(catId);
                }
            });
        }

        return categories;
    }

    const defaultFilter: ProductFilter = {
        ...setup.filter || {},
        minPrice: route.query.minPrice ? Number(route.query.minPrice) : undefined,
        maxPrice: route.query.maxPrice ? Number(route.query.maxPrice) : undefined,
        categories: getRouteCategories()
        // features: params.features ? getRouteArrayValues('features') : [],
        // ratings: params.ratings ? getRouteArrayValues('ratings').map(Number) : []
    }

    const filter = ref<ProductFilter>(defaultFilter)
    const page = ref(1);
    const perPage = ref(setup.perPage || config.perPage);
    const sortBy = ref<SortOptions>(setup.sortBy || 'relevance');
    const view = ref<'grid' | 'list'>(userPreferences.value.view || 'grid');

    const {
        locale
    } = useI18n();

    const query = computed(() => {
        const query : ProductQuery = {
            perPage: perPage.value,
            page: page.value
        };

        if (filter.value.minPrice !== undefined) {
            query.minPrice = filter.value.minPrice;
        }

        if (filter.value.maxPrice !== undefined) {
            query.maxPrice = filter.value.maxPrice;
        }

        if (filter.value.categoryId) {
            query.categoryId = filter.value.categoryId;
        }

        if (filter.value.collectionId) {
            query.collectionId = filter.value.collectionId;
        }

        if (filter.value.categories && filter.value.categories.length > 0) {
            query.categoryIds = filter.value.categories.join(',');
        }

        if (filter.value.collectionId) {
            query.collectionId = filter.value.collectionId;
        }

        if (sortBy.value == 'price_asc') {
            query.sortBy = 'price';
            query.sortDir = 'asc';
        } else if (sortBy.value == 'price_desc') {
            query.sortBy = 'price';
            query.sortDir = 'desc';
        } else if (sortBy.value == 'popularity') {
            query.sortBy = 'popularity';
        }

        return query
    })

    const catalogService = useCatalogService();
    
    const result = await useAsyncData(() => catalogService.getProducts(query.value), {
            watch: [query, locale],
            default: () => ({
                data: [],
                meta: {
                total: 0,
                minPrice: 0,
                maxPrice: 0
            }
        })
    })

    const reset = () => {
        filter.value = {
            ...setup.filter || {},
            categories: []
        }
        page.value = 1
        sortBy.value = setup.sortBy || 'relevance'
    }

    watch([filter, sortBy, perPage], () => {
        router.replace({
            query: {
                ...route.query,
                ...(filter.value.minPrice ? { minPrice: String(filter.value.minPrice) } : {}),
                ...(filter.value.maxPrice ? { maxPrice: String(filter.value.maxPrice) } : {}),
                ...(filter.value.categories && filter.value.categories.length > 0 ? { categories: filter.value.categories.map(String) } : {})
            }
        })
        page.value = 1
    })

    watch(view, (v) => {
        //Save view mode in user preferences cookie
        userPreferences.value.view = v;
    })

    return {
        filter,
        page,
        perPage,
        result: computed(() => ({
            products: result.data.value.data,
            meta: result.data.value.meta,
            pending: result.pending.value,
            error: result.error.value
        })),
        sortBy,
        view,
        reset
    }
}